import { useState, useMemo } from 'react';
import { Row, Col, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import _ from 'lodash'

const currency = window.location.hostname === 'seller.giftr.my' ? 'RM' : 'S$'

const emptyValue = () => ({ value: '', incremental_price: '' })

const VariantTemplateModal = ({ onHide, onSave, initialTemplate }) => {
  const [name, setName] = useState(initialTemplate ? initialTemplate.name : '')
  const [values, setValues] = useState(initialTemplate && initialTemplate.values.length ?
    _.map(initialTemplate.values, val => ({ value: val.value, incremental_price: val.incremental_price })) :
    [emptyValue()]
  )
  const [saving, setSaving] = useState(false)

  const valueError = useMemo(() => {
    let acc = []
    for (let val of values) {
      const value = _.trim(val.value)
      if (value && acc.includes(value)) {
        return `Option value "${value}" already in use.`
      }
      acc.push(value)
    }
    if (values.length > 100) {
      return 'A variant template cannot have more than 100 option values.'
    }
    return ''
  }, [values])

  const onChangeValue = (index, e) => {
    const { name, value } = e.target
    let updateValues = [...values]
    updateValues[index] = {
      ...updateValues[index],
      [name]: value
    }
    setValues(updateValues)
  }

  const addValue = () => {
    setValues([...values, emptyValue()])
  }

  const removeValue = (index) => {
    let updateValues = [...values]
    updateValues.splice(index, 1)
    if (updateValues.length === 0) {
      updateValues = [emptyValue()]
    }
    setValues(updateValues)
  }

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.target.reportValidity() || valueError) {
      return
    }

    setSaving(true)
    try {
      await onSave({
        name: _.trim(name),
        values: _.map(values, val => ({
          value: _.trim(val.value),
          incremental_price: parseFloat(val.incremental_price) || 0,
        }))
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={true} onHide={onHide} size="lg" style={{zIndex: '10000'}}>
      <Modal.Header>
        <h4 className="mb-0">{initialTemplate ? 'Edit Variant Template' : 'Create Variant Template'}</h4>
      </Modal.Header>
      <form onSubmit={submit}>
        <Modal.Body>
          <p className="text-muted">
            Save an option with its values and incremental prices. You can apply it to any product to generate its variants.
          </p>
          <Row className="d-grid gap-3 mb-2">
            <Col className="d-grid gap-1">
              <label>Option Name *</label>
              <input className="form-control" type="text" required placeholder="e.g. Size" value={name || ''} onChange={(e) => setName(e.target.value)}/>
            </Col>
          </Row>
          <div className='table-responsive'>
            <table className="table" style={{ fontSize: '0.9em' }}>
              <thead>
                <tr>
                  <th>Option Values *</th>
                  <th width="200">Incremental Price ({currency}) *</th>
                  <th width="80" className="text-center">Remove</th>
                </tr>
              </thead>
              <tbody>
                { values.map((val, i) => (
                  <tr key={i}>
                    <td>
                      <input className="form-control" type="text" name="value" required value={val.value || ''} onChange={(e) => onChangeValue(i, e)}/>
                    </td>
                    <td>
                      <input className="form-control" type="number" name="incremental_price" required min="0" step=".01" placeholder="0.00" value={val.incremental_price} onChange={(e) => onChangeValue(i, e)}/>
                    </td>
                    <td className="text-center" style={{verticalAlign: 'middle'}}>
                      <Button className="px-2 py-0" variant="danger" onClick={() => removeValue(i)}><FontAwesomeIcon icon={solid('close')} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {valueError && <div className="text-danger mb-2">{valueError}</div>}
            <div>
              <Button className="shadow-sm" variant="primary" onClick={addValue}><FontAwesomeIcon icon={solid('plus')} className="mx-1"/>&nbsp;Add Value</Button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
          <Button variant="primary" type="submit" disabled={!!valueError || saving}>
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default VariantTemplateModal
